import React from 'react'
import { FormattedMessage } from 'react-intl'
import CountUp from 'react-countup'
import { LearnMore } from 'src/constants/trackingEvents'
import AdditionalInfo from 'src/Components/shared/AdditionalInfo'
import Button from 'src/Components/shared/Button'
import Format from 'src/Components/shared/Format'
import { If } from 'src/Components'
import { IconEquals, IconMinus } from 'src/Components/shared/Icon'
import Lock from 'src/Components/shared/Lock'
import Text from 'src/Components/shared/Text'
import usePrev from 'src/hooks/usePrev'

import YourLoanSentence from 'src/Components/homeowners/YourLoanSentence'

import styles from './NetWorthSection.module.scss'

const NetWorthSection = ({
  homeValue,
  estimatedValue,
  timedEstimatedValue,
  hasLoans,
  outstandingPrincipal,
  showInfo,
  toggleInfo,
  withCurrentHomeData,
  locked
}) => {
  const prevEstimatedValue = usePrev(estimatedValue)

  return (
    <>
      <Text element='h3' size='l' weight='light' className={styles.heading}>
        <FormattedMessage
          id='Homeowner.estimatedValue.netWorthHeadline'
          defaultMessage='That means the net worth of the home to you is'
        />
      </Text>

      <Text
        size='xxl'
        color='pink'
        weight='light'
        className={styles.avm}
        element='div'
      >
        <Lock enabled={!withCurrentHomeData}>
          <CountUp
            start={prevEstimatedValue}
            end={estimatedValue}
            separator=','
            prefix='$'
          />
        </Lock>
      </Text>

      <If condition={estimatedValue > 1000}>
        <Text color='quiet' size='s' className={styles.soldText}>
          <FormattedMessage
            id='Homeowner.estimatedValue.equityExplanation'
            defaultMessage='If you sold your home today, this is approximately how much you would put in your pocket.'
          />
        </Text>
      </If>

      <If condition={withCurrentHomeData}>
        <Button
          onClick={toggleInfo}
          className={styles.expandButton}
          track={LearnMore}
          trackEnabled={!showInfo}
          trackOptions={{ eventLabel: 'EstValue-Equity' }}
        >
          <FormattedMessage
            id='Homeowner.estimatedValue.equityInfoToggleButton'
            defaultMessage='How is this calculated?'
          />
        </Button>

        <AdditionalInfo
          visible={showInfo}
          className={styles.additionalInfo}
          cardWidth
          cardBottom
        >
          <Text element='h3' weight='light'>
            <FormattedMessage
              id='Homeowner.estimatedValue.netWorthExplainHeadline'
              defaultMessage='Your net worth ("equity") is made up of two things.'
            />
          </Text>

          <div className={styles.equityEquation}>
            <div className={styles.equationGroup}>
              <div className={styles.equationGroupLabel}>
                <Text size='s' weight='light'>
                  <FormattedMessage
                    id='Homeowner.estimatedValue.homeValue'
                    defaultMessage='Home value'
                  />
                </Text>
              </div>
              <div className={styles.valueBoxEmphasized}>
                <Format.Currency value={homeValue} />
              </div>
            </div>
            <IconMinus className={styles.iconMinus} />
            <div className={styles.equationGroup}>
              <div className={styles.equationGroupLabel}>
                <Text size='s' weight='light'>
                  <FormattedMessage
                    id='Homeowner.estimatedValue.loanBalance'
                    defaultMessage='Loan balance'
                  />
                </Text>
              </div>
              <div className={styles.valueBox}>
                <Format.Currency value={Math.round(outstandingPrincipal)} />
              </div>
            </div>
            <IconEquals className={styles.iconEquals} />
            <div className={styles.equationGroup}>
              <div className={styles.equationGroupLabel}>
                <Text size='s' weight='light'>
                  <FormattedMessage
                    id='Homeowner.estimatedValue.yourNetWorth'
                    defaultMessage='Your net worth'
                  />
                </Text>
              </div>
              <div className={styles.valueBoxEmphasized}>
                <Format.Currency value={Math.round(estimatedValue)} />
              </div>
            </div>
          </div>

          <If condition={hasLoans}>
            <Text element='h3' weight='light' paragraph>
              <FormattedMessage
                id='Homeowner.estimatedValue.whereDoesInformationQ'
                defaultMessage='Where does this information come from?'
              />
            </Text>
          </If>

          <Text size='s'>
            <YourLoanSentence long />
          </Text>
        </AdditionalInfo>
      </If>
    </>
  )
}

export default NetWorthSection
